import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import { regions } from "../data/regions";
import { cityBySlug } from "../data/cities";
import { getHead } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, CTABand, SectionHead } from "../components/Blocks";

export function RegionsIndex() {
  const head = getHead("/regions");
  return (
    <>
      <Seo title={head.title} description={head.description} path="/regions" />
      <PageHero
        overline="Regional Coverage"
        title="Security Guard Services by Region"
        lead="Beyond individual cities, Maze Security Solutions runs coordinated multi-city programs across Canada's major metropolitan and economic regions — one contract, one dispatch line, verified reporting everywhere."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="regions-directory">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {regions.map((r) => (
            <Link
              key={r.slug}
              to={`/regions/${r.slug}`}
              data-testid={`region-card-${r.slug}`}
              className="group border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(255,85,0,0.15)]"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white group-hover:text-[#FF5500] transition-colors">{r.name}</h2>
                <ChevronRight className="h-5 w-5 text-[#FF5500] shrink-0" />
              </div>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">{r.blurb}</p>
              <p className="mt-3 text-xs font-mono uppercase tracking-wider text-gray-500">{r.cities.length} municipalities covered</p>
            </Link>
          ))}
        </div>
      </section>
      <CTABand title="Multi-Site, Multi-City, One Call" sub="Regional portfolios are our specialty — tell dispatch how many sites and cities, and we'll design the coverage." />
    </>
  );
}

export default function RegionPage({ region }) {
  const path = `/regions/${region.slug}`;
  const head = getHead(path, { region });
  return (
    <>
      <Seo title={head.title} description={head.description} path={path} />
      <section className="relative border-b border-white/10 overflow-hidden" data-testid="region-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-4">Regional Security Coverage</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-none text-white max-w-4xl">{region.h1}</h1>
          <p className="mt-5 text-lg text-gray-300 leading-relaxed max-w-3xl">{region.blurb}</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="region-intro">
        <div className="max-w-3xl space-y-5">
          {region.paras.map((p, i) => (
            <p key={i} className="text-base text-gray-400 leading-relaxed">{p}</p>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="region-cities">
        <SectionHead overline="Local Pages" title={`Cities & Communities in the ${region.name}`} />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {region.cities.map((slug) => {
            const c = cityBySlug[slug];
            if (!c) return null;
            return (
              <Link
                key={slug}
                to={`/security-guards-${slug}`}
                data-testid={`region-city-${slug}`}
                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors py-1"
              >
                <MapPin className="h-3 w-3 text-[#FF5500] shrink-0" /> {c.name}
              </Link>
            );
          })}
        </div>
      </section>
      <CTABand title={`Cover Every Site in the ${region.name}`} sub="One contract, one dispatch line, verified reporting at every location. Call or email to scope your regional coverage." />
    </>
  );
}
